import { GameMini } from 'src/components/common/GameMini/gameMini';
import { DotHeading } from 'src/components/common/texts/DotHeading/dotHeading';
import { FrameHeading } from 'src/components/common/texts/FrameHeading/frameHeading';
import { TextareaField } from 'src/components/common/inputs/InputField/textareaField';
import { InputField } from '../../../inputs/InputField/inputField';

import { FieldArray, useFormikContext } from 'formik';
import { Description } from './Description/description';
import { FC, useMemo } from 'react';
import { CartridgeInfoType } from '../SetCartridgeType/setCartridgeType';
import { SocialMedia } from './SocialMedia/socialMedia';
import { ViewScreenshots } from './ViewScreenshots/viewScreenshots';

import { useTranslation } from 'react-i18next';
import { TwoColumn } from 'src/components/common/CartridgeForm/containers/TwoColumn/twoColumn';
import { LeftColumn } from 'src/components/common/CartridgeForm/containers/TwoColumn/leftColumn';
import { RightColumn } from 'src/components/common/CartridgeForm/containers/TwoColumn/rightColumn';
import { SmallHeading } from 'src/components/common/texts/SmallHeading/smallHeading';
import { placeholderUrl, PlatformIconAssets } from 'src/config';
import { Stages, useCartridgeCreatorProvider } from 'src/providers/Cartridge/cartridgeProvider';
import { DeployButton } from './DeployButton';
import { ITag, TagSelector } from '../../../inputs/TagSelector/tagSelector';
import { Image } from '@chakra-ui/react';
import { FieldGroup } from '../../containers/FieldGroup/fieldGroup';
import { DeploymentOverlay } from './DeploymentOverlay/deploymentOverlay';

export const availableCategories: ITag[] = [
	{ content: 'RPG', value: 'rpg' },
	{ content: 'Action', value: 'action' },
	{ content: 'Puzzle', value: 'puzzle' },
	{ content: 'Sports', value: 'sports' },
	{ content: 'Casual', value: 'casual' },
	{ content: 'Multiplayer', value: 'multiplayer' },
	{ content: '3D', value: '3d' },
	{ content: '2D', value: '2d' },
	{ content: 'Pixel art', value: 'pixelArt' },
	{ content: 'Sandbox', value: 'sandbox' },
	{ content: 'Fighting', value: 'fighting' },
	{ content: 'Shooter', value: 'shooter' },
	{ content: 'Survival', value: 'survival' },
	{ content: 'FPS', value: 'fps' },
	{ content: 'Medieval', value: 'medieval' },
	{ content: 'Cartoon', value: 'cartoon' },
	{ content: 'Idle', value: 'idle' },
	{ content: 'Roguelite', value: 'roguelite' },
	{ content: 'Visual novel', value: 'visualNovel' },
	{ content: 'Zombies', value: 'zombies' },
	{ content: 'Thriller', value: 'thriller' },
	{ content: 'Logic', value: 'logic' },
	{ content: 'Board game', value: 'boardGame' },
	{ content: 'Science', value: 'science' },
	{ content: 'Cooking', value: 'cooking' },
	{ content: 'Music', value: 'music' },
	{ content: 'Tower defense', value: 'towerDefense' },
	{ content: 'Indie', value: 'indie' },
	{ content: 'Anime', value: 'anime' },
	{ content: 'Singleplayer', value: 'singleplayer' },
	{ content: 'Fantasy', value: 'fantasy' },
	{ content: 'Horror', value: 'horror' },
	{ content: 'Interactive', value: 'interactive' },
	{ content: 'First person', value: 'firstPerson' },
	{ content: 'Open world', value: 'openWorld' },
	{ content: 'Online', value: 'online' },
	{ content: 'Racing', value: 'racing' },
	{ content: 'Simulation', value: 'simulation' },
	{ content: 'Adventure', value: 'adventure' },
	{ content: 'Strategy', value: 'strategy' },
	{ content: 'Platformer', value: 'platformer' },
	{ content: 'MMO', value: 'mmo' },
	{ content: 'Sci-fi', value: 'scifi' },
	{ content: 'Relaxing', value: 'relaxing' },
	{ content: 'Co-op', value: 'coop' },
	{ content: 'VR', value: 'vr' },
];

export const availablePlatforms: ITag[] = [
	{ content: <Image src={PlatformIconAssets.pc} />, value: 'pc' },
	{ content: <Image src={PlatformIconAssets.html} />, value: 'html' },
];

interface Props {
	selectedCartridge: CartridgeInfoType;
	isValid: () => Promise<boolean>;
}

export const SetMetadata: FC<Props> = ({ selectedCartridge, isValid }) => {
	const { values } = useFormikContext();
	const { t } = useTranslation();
	const { stage } = useCartridgeCreatorProvider();

	const metadataFinished = useMemo(() => stage > Stages.UNDEPLOYED, [stage]);

	return (
		<TwoColumn>
			<LeftColumn>
				<FieldGroup>
					<DotHeading
						content={t('form.headers.metadata.title')}
						description={t('form.headers.metadata.description')}
					/>
					<TagSelector
						tags={availableCategories}
						label="Category"
						name="genre"
					/>
					<TagSelector
						tags={availablePlatforms}
						label="Platforms"
						name="platforms"
					/>
				</FieldGroup>
				<FieldGroup>
					<TextareaField
						name="description"
						placeholder=""
						label={t('form.fields.description')}
					/>
				</FieldGroup>
				<FieldGroup>
					<InputField
						fixedLeftWidth
						name="socials.facebook"
						label="Social media"
						disabled={metadataFinished}
						placeholder="https://www.facebook.com/yourgame"
						leftText="Facebook"
					/>
					<InputField
						fixedLeftWidth
						name="socials.twitter"
						disabled={metadataFinished}
						placeholder="https://twitter.com/yourgame"
						leftText="Twitter"
					/>
					<InputField
						fixedLeftWidth
						name="socials.youtube"
						disabled={metadataFinished}
						placeholder="https://www.youtube.com/c/yourchannel"
						leftText="Youtube"
					/>
					<InputField
						fixedLeftWidth
						name="socials.discord"
						disabled={metadataFinished}
						placeholder="https://discord.gg/00000000"
						leftText="Discord"
					/>
				</FieldGroup>
				<FieldGroup>
					<InputField
						label={t('form.fields.videoPreview')}
						name="video"
						placeholder="https://www.youtube.com/watch?v=00000000000"
						leftText="Youtube URL"
						disabled={metadataFinished}
					/>
				</FieldGroup>
				<SmallHeading text={t('form.fields.screenshots')} />

				<FieldGroup>
					<DotHeading content="Gamepaper" />
					<InputField
						name="gamepaper"
						disabled={metadataFinished}
						placeholder={placeholderUrl('gamepaper.pdf')}
						label={t('form.fields.gamepaper')}
						leftText="URL"
					/>
				</FieldGroup>
				<DeployButton isValid={isValid} />
				<DeploymentOverlay />
			</LeftColumn>
			<RightColumn>
				<FrameHeading content={t('form.headers.metadataVisualization')} />
				<GameMini
					color={selectedCartridge.color}
					address=""
					title={(values as any).gameTitle}
					abbreviation={(values as any).abbreviation}
					logoUrl={(values as any).media.logoUrl}
				/>
				<SocialMedia socialsList={(values as any).socials} />
				<Description description={(values as any).description} />
				<ViewScreenshots />
			</RightColumn>
		</TwoColumn>
	);
};
